import { logger } from '@/core/logger';

/**
 * Configuration options for LocalStorageCache
 */
interface LocalStorageCacheConfig {
	/**
	 * Storage key for localStorage (e.g., 'user_organization', 'user_projects')
	 */
	storageKey: string;
}

/**
 * Generic LocalStorage cache utility with error handling and logging
 *
 * @example
 * ```typescript
 * // Create a cache for organizations
 * const orgCache = new LocalStorageCache<Organization>({
 *   storageKey: 'user_organization',
 * });
 *
 * // Save data
 * orgCache.save(myOrganization);
 *
 * // Get data (returns null if not found)
 * const org = orgCache.get();
 *
 * // Clear cache
 * orgCache.clear();
 * ```
 */
export class LocalStorageCache<T> {
	private readonly storageKey: string;

	constructor(config: LocalStorageCacheConfig) {
		this.storageKey = config.storageKey;
	}

	/**
	 * Save data to localStorage
	 * @param data - The data to cache
	 */
	save(data: T): void {
		try {
			localStorage.setItem(this.storageKey, JSON.stringify(data));

			logger.info(`${this.storageKey} saved to cache`);
		} catch (error) {
			// Handle quota exceeded or access denied errors
			logger.error(
				`Failed to save ${this.storageKey} to localStorage`,
				error
			);
		}
	}

	/**
	 * Get data from localStorage
	 * @returns Cached data or null if not found/invalid
	 */
	get(): T | null {
		try {
			const stored = localStorage.getItem(this.storageKey);

			if (!stored) {
				return null;
			}

			const parsed = JSON.parse(stored);

			// Evict legacy {data, timestamp} wrapper written by the old TTL implementation.
			// Without this, existing users would get a wrong-shaped object silently cast to T.
			if (
				parsed !== null &&
				typeof parsed === 'object' &&
				'data' in parsed &&
				'timestamp' in parsed &&
				typeof (parsed as Record<string, unknown>).timestamp ===
					'number'
			) {
				logger.info(
					`${this.storageKey} legacy cache format detected, clearing`
				);
				this.clear();
				return null;
			}

			logger.debug(`${this.storageKey} cache hit`);

			return parsed as T;
		} catch (error) {
			// Handle invalid JSON or parsing errors
			logger.error(
				`Failed to parse ${this.storageKey} from localStorage`,
				error
			);
			// Clear corrupted data
			this.clear();
			return null;
		}
	}

	/**
	 * Clear cache
	 * Called when data becomes invalid or on logout
	 */
	clear(): void {
		try {
			localStorage.removeItem(this.storageKey);
			logger.info(`${this.storageKey} cache cleared`);
		} catch (error) {
			logger.error(
				`Failed to clear ${this.storageKey} from localStorage`,
				error
			);
		}
	}

	/**
	 * Check if data exists in cache
	 * @returns true if cached data exists
	 */
	has(): boolean {
		return !!this.get();
	}
}
